import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Stats, Lab } from '../../assets/icons';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import '../../assets/css/lab/dashboard.css';
import { casesApi } from '../../network/apis/cases';
import VisitorStats from '../../components/elements/visitorStats';
import LoadingSpinner from '../../components/elements/LoadingSpinner';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const LabDashboard = () => {
    const navigate = useNavigate();
    const chartRef = useRef(null);
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const user = JSON.parse(localStorage.getItem('user'));
    const [labName] = useState(user?.lab_name || user?.first_name || 'Lab');

    // Chart data based on cases
    const chartData = {
        labels: ['Completed', 'Processing', 'Pending'],
        datasets: [
            {
                data: [
                    cases.filter(c => c.status?.toLowerCase() === 'resolved' || c.status?.toLowerCase() === 'completed').length,
                    cases.filter(c => c.status?.toLowerCase() === 'in-progress' || c.status?.toLowerCase() === 'processing').length,
                    cases.filter(c => c.status?.toLowerCase() === 'pending' || c.status?.toLowerCase() === 'open').length
                ],
                backgroundColor: ['#10b981', '#3b82f6', '#f59e0b'],
                hoverBackgroundColor: ['#059669', '#2563eb', '#d97706'],
            },
        ],
    };

    useEffect(() => {
        fetchCases();
    }, []);

    const fetchCases = async () => {
        try {
            setLoading(true);
            const response = await casesApi.getCases();
            if (response.data.data) {
                setCases(response.data.data || []);
            } else {
                setCases([]);
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch cases');
            setCases([]);
        } finally {
            setLoading(false);
        }
    };

    const statsData = {
        total: cases.length,
        pending: cases.filter(c => c.status?.toLowerCase() === 'pending' || c.status?.toLowerCase() === 'open').length,
        processing: cases.filter(c => c.status?.toLowerCase() === 'in-progress' || c.status?.toLowerCase() === 'processing').length,
        completed: cases.filter(c => c.status?.toLowerCase() === 'resolved' || c.status?.toLowerCase() === 'completed').length,
        highPriority: cases.filter(c => c.priority?.toLowerCase() === 'high').length
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Something went wrong</h2>
                <p>{error}</p>
                <button className="btn-primary" onClick={fetchCases}>
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="lab-dashboard">
            <header className="dashboard-header">
                <div className="welcome-section">
                    <h1>Welcome, {labName}</h1>
                    <p>Laboratory Dashboard</p>
                </div>
                
                {/* <div className="header-actions">
                    <RoleSwitcher />
                </div> */}
            </header>

            <div className="dashboard-stats">
                <div className="stat-card" onClick={() => navigate('/lab/cases')}>
                    <div className="stat-icon">
                        <img src={Lab} alt="Total Samples" />
                    </div>
                    <div className="stat-content">
                        <h4>Assigned Cases</h4>
                        <span className="stat-number">{statsData.total}</span>
                        <span className="stat-label">Total cases assigned to lab</span>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon">
                        <img src={Stats} alt="Pending" />
                    </div>
                    <div className="stat-content">
                        <h4>Pending Analysis</h4>
                        <span className="stat-number">{statsData.pending}</span>
                        <span className="stat-label">Awaiting processing</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <img src={Stats} alt="Completed" />
                    </div>
                    <div className="stat-content">
                        <h4>Completed Tests</h4>
                        <span className="stat-number">{statsData.completed}</span>
                        <span className="stat-label">Results submitted</span>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon">
                        <img src={Clock} alt="Turnaround" />
                    </div>
                    <div className="stat-content">
                        <h4>Avg. Turnaround</h4>
                        <span className="stat-number">24h</span>
                        <span className="stat-label">Average processing time</span>
                    </div>
                </div>
            </div>

            <div className="platform-activity">
                <h3>Platform Activity</h3>
                <VisitorStats variant="standard" />
            </div>

            <div className="summary-section">
                <div className="summary-grid">
                    <div className="summary-table">
                        <h3>Sample Status Summary</h3>
                        <table>
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Count</th>
                                    <th>Percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        <span className="status-dot pending"></span>
                                        Pending
                                    </td>
                                    <td>{statsData.pending}</td>
                                    <td>{statsData.total > 0 ? Math.round((statsData.pending / statsData.total) * 100) : 0}%</td>
                                </tr>
                                <tr>
                                    <td>
                                        <span className="status-dot processing"></span>
                                        Processing
                                    </td>
                                    <td>{statsData.processing}</td>
                                    <td>{statsData.total > 0 ? Math.round((statsData.processing / statsData.total) * 100) : 0}%</td>
                                </tr>
                                <tr>
                                    <td>
                                        <span className="status-dot completed"></span>
                                        Completed
                                    </td>
                                    <td>{statsData.completed}</td>
                                    <td>{statsData.total > 0 ? Math.round((statsData.completed / statsData.total) * 100) : 0}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="summary-chart">
                        <h3>Status Distribution</h3>
                        <div className="chart-container">
                            <Doughnut 
                                ref={chartRef}
                                key="lab-dashboard-chart"
                                data={chartData}
                                options={{
                                    cutout: '70%',
                                    responsive: true,
                                    maintainAspectRatio: true,
                                    plugins: {
                                        legend: {
                                            position: 'bottom',
                                            labels: {
                                                boxWidth: 12,
                                                padding: 15,
                                                font: {
                                                    size: 12
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LabDashboard;